import type { FileRoutesByFullPath } from '@/routeTree.gen'
import type { SupportedLanguage } from '@/modules/i18n/supportedLanguages'

const SITE_URL = 'https://www.xp-lab.com'

type CanonicalRoutePath = {
  [K in keyof FileRoutesByFullPath]: K extends `/$lang${string}` ? never : K
}[keyof FileRoutesByFullPath]

export interface PrerenderedPage {
  /** Canonical (English) path. Must match a TanStack route in routeTree.gen.ts. */
  path: CanonicalRoutePath
  /** Output directory under dist/apps/client. '.' for homepage. */
  outDir: string
  /**
   * When false, only the English (canonical) URL is prerendered — for in-app
   * pages that have no `/$lang/...` localized route (e.g. /challenges/available).
   * Defaults to true (marketing pages prerender in all locales). An en-only page
   * also suppresses he/de sitemap entries and hreflang alternates.
   */
  localized?: boolean
}

/**
 * Single source of truth for the prerendered public-page set.
 * Imported directly by apps/client/scripts/prerender.ts and
 * apps/client/scripts/generate-seo-files.ts (run via tsx).
 */
export const PRERENDERED_PAGES = [
  { path: '/',                                              outDir: '.' },
  { path: '/about',                                         outDir: 'about' },
  { path: '/faq',                                           outDir: 'faq' },
  { path: '/for-employers',                                 outDir: 'for-employers' },
  { path: '/for-education',                                 outDir: 'for-education' },
  { path: '/take-home-assignments',                         outDir: 'take-home-assignments' },
  { path: '/xp-lab-feedback',                               outDir: 'xp-lab-feedback' },
  { path: '/using-ai-without-outsourcing-judgment',         outDir: 'using-ai-without-outsourcing-judgment' },
  { path: '/analytical-workflow',                           outDir: 'analytical-workflow' },
  { path: '/challenges/available',                         outDir: 'challenges/available', localized: false },
  { path: '/privacy-policy',                                outDir: 'privacy-policy' },
  { path: '/terms-of-service',                              outDir: 'terms-of-service' },
] as const satisfies readonly PrerenderedPage[]

export const PRERENDERED_PATHS: readonly string[] = PRERENDERED_PAGES.map((p) => p.path)

/**
 * Whether a prerendered page expands to all locales. Typed against the full
 * `PrerenderedPage` (required path/outDir) rather than `{ localized?: boolean }`
 * so the `as const` page literals — which omit the optional `localized` key —
 * are assignable (a param with only optional props is a "weak type" that
 * rejects objects sharing no property).
 */
export function isPageLocalized(page: PrerenderedPage): boolean {
  return page.localized !== false
}

const ENGLISH_ONLY_PATHS = new Set(
  PRERENDERED_PAGES.filter((p) => !isPageLocalized(p)).map((p) => p.path as string)
)

/**
 * True for prerendered pages that exist only at their canonical English URL
 * (no `/$lang/...` route). Such pages must not advertise he/de hreflang
 * alternates, since those URLs would 404.
 */
export function isEnglishOnlyPath(canonicalPath: string): boolean {
  return ENGLISH_ONLY_PATHS.has(canonicalPath)
}

const PREFIXED_LANGS = ['he', 'de'] as const
type PrefixedLang = (typeof PREFIXED_LANGS)[number]

function isPrefixedLang(lang: string): lang is PrefixedLang {
  return (PREFIXED_LANGS as readonly string[]).includes(lang)
}

export function parseLangPrefix(pathname: string): { lang: PrefixedLang; rest: string } | null {
  const match = pathname.match(/^\/(he|de)(\/.*)?$/)
  if (!match) return null
  const lang = match[1] as PrefixedLang
  const rest = match[2] ?? '/'
  return { lang, rest }
}

export function isPrerenderedPath(pathname: string): boolean {
  const prefix = parseLangPrefix(pathname)
  const canonical = prefix ? prefix.rest : pathname
  if (!PRERENDERED_PATHS.includes(canonical)) return false
  // en-only pages are prerendered only at the canonical (non-prefixed) URL,
  // so a /he|/de prefixed form of one is NOT a prerendered path.
  if (prefix && ENGLISH_ONLY_PATHS.has(canonical)) return false
  return true
}

export function swapLangInPath(pathname: string, target: SupportedLanguage): string {
  const prefix = parseLangPrefix(pathname)
  const canonical = prefix ? prefix.rest : pathname
  if (!PRERENDERED_PATHS.includes(canonical)) return pathname

  // en-only pages have no localized URL — switching language keeps the
  // canonical path (UI language is a user setting, not a URL prefix).
  if (ENGLISH_ONLY_PATHS.has(canonical)) return canonical

  if (target === 'en') {
    return canonical
  }
  // Avoid '/he/' for the homepage — emit '/he', not '/he/'.
  return canonical === '/' ? `/${target}` : `/${target}${canonical}`
}

export function localizedUrlsForPath(canonicalPath: string): Record<SupportedLanguage, string> {
  const en = `${SITE_URL}${canonicalPath}`
  const he = canonicalPath === '/' ? `${SITE_URL}/he` : `${SITE_URL}/he${canonicalPath}`
  const de = canonicalPath === '/' ? `${SITE_URL}/de` : `${SITE_URL}/de${canonicalPath}`
  return { en, he, de }
}

export { isPrefixedLang }
